import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AnalyticsSidebar from './layout/AnalyticsSidebar';
import DashboardLoadingShell from './layout/DashboardLoadingShell';
import { DashboardContainer, MainContent, TopBar, PageContent, ContentSection } from './layout/AppLayout';
import logoImage from '../assets/images/STS_Logo.png';
import { AnnouncementCard, AnnouncementEmptyState } from './AnnouncementCard';
import ConfirmModal from './ConfirmModal';
import {
  getAnnouncementUserId,
  getAnnouncementUserName,
  isAnnouncementStatusError,
  removeAnnouncementFromCollection,
  updateAnnouncementCollection,
} from './announcementDashboard.utils';
import { canAccessRole, normalizeRole } from './manageUsers.utils';
import { apiUrl } from '../api';
import { buildAuthHeaders, getStoredUserSession } from './session.utils';

const API_BASE = apiUrl('').replace(/\/$/, '');

const readJsonResponse = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    throw new Error('Malformed announcement response.');
  }
};

const readAnnouncementCollection = async (response) => {
  if (!response.ok) {
    throw new Error(`Announcements request failed with status ${response.status}`);
  }

  const data = await response.json();
  if (!Array.isArray(data)) {
    throw new Error('Announcements response must be a list.');
  }

  return data;
};

function AnnouncementListSkeleton({ count = 2 }) {
  return (
    <div className="announcement-list" aria-label="Loading announcements">
      {Array.from({ length: count }, (_, index) => (
        <article className="announcement-card announcement-skeleton" key={`announcement-skeleton-${index}`}>
          <div className="announcement-card-icon" aria-hidden="true" />
          <div className="announcement-card-body" aria-hidden="true">
            <div className="announcement-card-meta-row">
              <span className="announcement-skeleton-line announcement-skeleton-badge" />
              <span className="announcement-skeleton-line announcement-skeleton-date" />
            </div>
            <span className="announcement-skeleton-line announcement-skeleton-title" />
            <span className="announcement-skeleton-line announcement-skeleton-message" />
            <span className="announcement-skeleton-line announcement-skeleton-message announcement-skeleton-message-short" />
          </div>
        </article>
      ))}
    </div>
  );
}

const pageConfig = {
  admin: {
    requiredRole: 'admin',
    sidebarRole: 'admin',
    activeItem: 'announcements',
    portalLabel: 'Admin Portal',
    heading: 'Announcements',
    subheading: 'Create and manage announcements for teacher accounts.',
    targetRole: 'teacher',
    creatorRole: 'admin',
    formTitle: 'Teacher Announcement',
    formHeading: 'Post an update for teachers',
    editHeading: 'Edit teacher announcement',
    formHelp: 'Share school reminders, lesson updates, or dashboard notices with teacher accounts.',
    messagePlaceholder: 'Write a message for teachers',
    buttonLabel: 'Post to Teachers',
    emptyTitle: 'No teacher announcements posted yet',
    emptyMessage: 'Announcements you post for teachers will appear here for editing or deletion.',
    listTitle: 'Manage Teacher Announcements',
    fallbackAuthor: 'Admin',
    icon: 'A',
  },
  teacher: {
    requiredRole: 'teacher',
    sidebarRole: 'teacher',
    activeItem: 'announcements',
    portalLabel: 'Teacher Portal',
    heading: 'Announcements',
    subheading: 'Review admin updates and post announcements for parent accounts.',
    targetRole: 'parent',
    creatorRole: 'teacher',
    formTitle: 'Parent Announcement',
    formHeading: 'Post an update for parents',
    editHeading: 'Edit parent announcement',
    formHelp: 'Keep families informed about new lessons, activities, reminders, and student support updates.',
    messagePlaceholder: 'Write a message for parents',
    buttonLabel: 'Post to Parents',
    emptyTitle: 'No parent announcements posted yet',
    emptyMessage: 'Announcements you post for parents will appear here for editing or deletion.',
    listTitle: 'Manage Parent Announcements',
    fallbackAuthor: 'Teacher',
    icon: 'T',
  },
  parent: {
    requiredRole: 'parent',
    sidebarRole: 'parent',
    activeItem: 'announcements',
    portalLabel: 'Parent Portal',
    heading: 'Announcements',
    subheading: 'Read the latest updates and reminders from teachers.',
    targetRole: 'parent',
    creatorRole: 'teacher',
    readOnly: true,
    emptyTitle: 'No teacher announcements yet',
    emptyMessage: "Updates from your child's teacher will appear here when available.",
    listTitle: 'Teacher Announcements',
    fallbackAuthor: 'Teacher',
  },
};

const buildDeleteUrl = (announcementId, useTeacherScope) => {
  const url = new URL(`${API_BASE}/api/announcements/${announcementId}`, window.location.origin);
  if (useTeacherScope) url.searchParams.set('scope', 'teacher');
  return url.toString();
};

const withTeacherScope = (url, useTeacherScope) => (
  useTeacherScope ? `${url}${url.includes('?') ? '&' : '?'}scope=teacher` : url
);

export default function AnnouncementPage({ mode = 'parent' }) {
  const navigate = useNavigate();
  const config = pageConfig[mode] || pageConfig.parent;
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [adminAnnouncements, setAdminAnnouncements] = useState([]);
  const [form, setForm] = useState({ title: '', message: '' });
  const [fieldErrors, setFieldErrors] = useState({ title: '', message: '' });
  const [validationActive, setValidationActive] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);

  const sessionRole = normalizeRole(user?.role || getStoredUserSession()?.role);
  const sidebarRole = sessionRole === 'parent_teacher' ? 'parent_teacher' : config.sidebarRole;
  const useTeacherScope = sessionRole === 'parent_teacher' && mode === 'teacher';
  const statusType = isAnnouncementStatusError(status) ? 'error' : 'success';

  const actorId = useMemo(() => getAnnouncementUserId(user), [user]);

  const loadAnnouncements = async (accountId) => {
    setAnnouncementsLoading(true);

    try {
      if (mode === 'teacher') {
        const [adminResult, ownResult] = await Promise.allSettled([
          fetch(`${API_BASE}/api/announcements?target_role=teacher&limit=20`).then(readAnnouncementCollection),
          fetch(`${API_BASE}/api/announcements?target_role=parent&created_by=${accountId}&created_by_role=teacher&limit=20`).then(readAnnouncementCollection),
        ]);

        setAdminAnnouncements(adminResult.status === 'fulfilled' ? adminResult.value : []);
        setAnnouncements(ownResult.status === 'fulfilled' ? ownResult.value : []);
        if (adminResult.status === 'rejected' || ownResult.status === 'rejected') {
          setStatus('Failed to load announcements.');
        }
        return;
      }

      const query = config.readOnly
        ? `${API_BASE}/api/announcements?target_role=${config.targetRole}&limit=20`
        : `${API_BASE}/api/announcements?target_role=${config.targetRole}&created_by=${accountId}&created_by_role=${config.creatorRole}&limit=20`;
      const response = await fetch(query);
      setAnnouncements(await readAnnouncementCollection(response));
    } catch (err) {
      console.error('Failed to load announcements:', err);
      setStatus('Failed to load announcements.');
      setAnnouncements([]);
      setAdminAnnouncements([]);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  useEffect(() => {
    const loadPage = async () => {
      try {
        const savedTheme = localStorage.getItem('theme') || 'light';
        document.documentElement.setAttribute('data-theme', savedTheme);

        const stored = JSON.parse(localStorage.getItem('loggedInUser') || 'null');
        const role = normalizeRole(stored?.role);
        const hasAccess = canAccessRole(role, config.requiredRole);
        if (!stored?.id || !hasAccess) {
          navigate('/login');
          return;
        }

        setUser({ ...stored, role });
        loadAnnouncements(stored.id);
      } catch (err) {
        console.error('Announcement page load failed:', err);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [config.requiredRole, navigate]);

  const resetComposer = () => {
    setForm({ title: '', message: '' });
    setFieldErrors({ title: '', message: '' });
    setValidationActive(false);
    setEditingAnnouncement(null);
    setStatus('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = {
      title: form.title.trim() ? '' : 'Please fill out this field.',
      message: form.message.trim() ? '' : 'Please fill out this field.',
    };
    setValidationActive(true);
    setFieldErrors(errors);
    if (errors.title || errors.message) {
      setStatus('');
      return;
    }
    if (!actorId) {
      setStatus('Your account could not be verified. Please sign in again.');
      return;
    }

    setSaving(true);
    setStatus('');
    try {
      const isEditing = Boolean(editingAnnouncement?.id);
      const requestUrl = withTeacherScope(
        isEditing ? `${API_BASE}/api/announcements/${editingAnnouncement.id}` : `${API_BASE}/api/announcements`,
        useTeacherScope
      );
      const response = await fetch(requestUrl, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { ...buildAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          message: form.message,
          target_role: config.targetRole,
        }),
      });
      const data = response.ok
        ? await readJsonResponse(response)
        : await readJsonResponse(response).catch(() => null);
      if (!response.ok) {
        setStatus(data?.error || (isEditing ? 'Failed to update announcement.' : 'Failed to post announcement.'));
        return;
      }

      setAnnouncements((prev) => updateAnnouncementCollection(prev, data));
      setForm({ title: '', message: '' });
      setFieldErrors({ title: '', message: '' });
      setValidationActive(false);
      setEditingAnnouncement(null);
      setStatus(isEditing ? 'Announcement updated.' : 'Announcement posted successfully.');
    } catch (err) {
      const fallbackStatus = editingAnnouncement?.id ? 'Failed to update announcement.' : 'Failed to post announcement.';
      setStatus(err.message === 'Malformed announcement response.' ? fallbackStatus : 'Connection error while saving announcement.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setForm({ title: announcement.title || '', message: announcement.message || '' });
    setFieldErrors({ title: '', message: '' });
    setValidationActive(false);
    setStatus('');
  };

  const handleDelete = (announcement) => {
    if (!actorId || !announcement?.id || saving) return;
    setAnnouncementToDelete(announcement);
  };

  const confirmDeleteAnnouncement = async () => {
    const announcement = announcementToDelete;
    if (!actorId || !announcement?.id || saving) return;

    setSaving(true);
    setStatus('');
    try {
      const response = await fetch(buildDeleteUrl(announcement.id, useTeacherScope), {
        method: 'DELETE',
        headers: { ...buildAuthHeaders(), 'Content-Type': 'application/json' },
      });
      const data = response.ok
        ? await readJsonResponse(response)
        : await readJsonResponse(response).catch(() => null);
      if (!response.ok) {
        setStatus(data?.error || 'Failed to delete announcement.');
        return;
      }

      setAnnouncements((prev) => removeAnnouncementFromCollection(prev, announcement.id));
      if (editingAnnouncement?.id === announcement.id) {
        resetComposer();
      }
      setAnnouncementToDelete(null);
      setStatus('Announcement deleted.');
    } catch (err) {
      setStatus('Connection error while deleting announcement.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLoadingShell
        role={sidebarRole}
        activeItem={config.activeItem}
        logoSrc={logoImage}
        portalLabel={config.portalLabel}
        heading={config.heading}
        subheading={config.subheading}
      />
    );
  }

  return (
    <>
      <DashboardContainer
      sidebar={
        <AnalyticsSidebar
          role={sidebarRole}
          activeItem={config.activeItem}
          logoSrc={logoImage}
          portalLabel={config.portalLabel}
        />
      }
      main={
        <MainContent>
          <TopBar>
            <div className="header-info">
              <h1>{config.heading}</h1>
              <p>{config.subheading}</p>
            </div>
          </TopBar>

          <PageContent>
            {status && (
              <div className={`announcement-banner announcement-banner-${statusType}`} aria-live="polite" role="status">
                {status}
              </div>
            )}

            {mode === 'teacher' && (
              <ContentSection title="Admin Announcements">
                {announcementsLoading ? (
                  <AnnouncementListSkeleton count={1} />
                ) : adminAnnouncements.length === 0 ? (
                  <AnnouncementEmptyState
                    title="No admin announcements yet"
                    message="New school-wide updates from the admin office will appear here."
                  />
                ) : (
                  <div className="announcement-list">
                    {adminAnnouncements.map((announcement, index) => (
                      <AnnouncementCard
                        key={announcement.id}
                        announcement={announcement}
                        fallbackAuthor="Admin"
                        highlight={index === 0}
                      />
                    ))}
                  </div>
                )}
              </ContentSection>
            )}

            {!config.readOnly && (
              <ContentSection title={config.formTitle}>
                <form noValidate className="announcement-form announcement-composer" onSubmit={handleSubmit}>
                  <div className="announcement-composer-header">
                    <div className="announcement-composer-icon" aria-hidden="true">{config.icon}</div>
                    <div>
                      <h3>{editingAnnouncement ? config.editHeading : config.formHeading}</h3>
                      <p>{editingAnnouncement ? 'Update the announcement title or message and save the changes.' : config.formHelp}</p>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      className={`input-field ${fieldErrors.title ? 'announcement-field-invalid' : ''}`}
                      value={form.title}
                      onChange={(event) => {
                        const value = event.target.value;
                        setForm((prev) => ({ ...prev, title: value }));
                        if (validationActive) setFieldErrors((prev) => ({ ...prev, title: value.trim() ? '' : 'Please fill out this field.' }));
                      }}
                      placeholder="Announcement title"
                      maxLength={150}
                      aria-invalid={Boolean(fieldErrors.title)}
                      aria-describedby={fieldErrors.title ? 'announcement-title-error' : undefined}
                    />
                    {fieldErrors.title && <p id="announcement-title-error" className="field-error" role="alert">{fieldErrors.title}</p>}
                  </div>
                  <div className="form-group">
                    <label>Message</label>
                    <textarea
                      className={`textarea-field ${fieldErrors.message ? 'announcement-field-invalid' : ''}`}
                      value={form.message}
                      onChange={(event) => {
                        const value = event.target.value;
                        setForm((prev) => ({ ...prev, message: value }));
                        if (validationActive) setFieldErrors((prev) => ({ ...prev, message: value.trim() ? '' : 'Please fill out this field.' }));
                      }}
                      placeholder={config.messagePlaceholder}
                      rows={4}
                      aria-invalid={Boolean(fieldErrors.message)}
                      aria-describedby={fieldErrors.message ? 'announcement-message-error' : undefined}
                    />
                    {fieldErrors.message && <p id="announcement-message-error" className="field-error" role="alert">{fieldErrors.message}</p>}
                  </div>
                  <div className="announcement-actions">
                    <button type="submit" className="btn-primary announcement-submit" disabled={saving}>
                      {saving ? 'Saving...' : editingAnnouncement ? 'Save Changes' : config.buttonLabel}
                    </button>
                    {editingAnnouncement && (
                      <button type="button" className="btn-secondary announcement-submit" onClick={resetComposer} disabled={saving}>
                        Cancel Edit
                      </button>
                    )}
                  </div>
                </form>
              </ContentSection>
            )}

            <ContentSection title={config.listTitle}>
              {announcementsLoading ? (
                <AnnouncementListSkeleton />
              ) : announcements.length === 0 ? (
                <AnnouncementEmptyState
                  title={config.emptyTitle}
                  message={config.emptyMessage}
                />
              ) : (
                <div className="announcement-list">
                  {announcements.map((announcement, index) => (
                    <AnnouncementCard
                      key={announcement.id}
                      announcement={announcement}
                      fallbackAuthor={getAnnouncementUserName(user, config.fallbackAuthor)}
                      highlight={index === 0}
                      onEdit={!config.readOnly ? handleEdit : undefined}
                      onDelete={!config.readOnly ? handleDelete : undefined}
                      actionDisabled={saving}
                    />
                  ))}
                </div>
              )}
            </ContentSection>
          </PageContent>
        </MainContent>
      }
      />
      <ConfirmModal
        open={Boolean(announcementToDelete)}
        title="Confirm Delete"
        message="Delete this announcement permanently?"
        confirmLabel="OK"
        cancelLabel="Cancel"
        variant="danger"
        busy={saving}
        onCancel={() => setAnnouncementToDelete(null)}
        onConfirm={confirmDeleteAnnouncement}
      />
    </>
  );
}
